import * as c from 'common/c'
import { get, findIndex, cloneDeep } from 'es-toolkit/compat'
import type LineString from 'ol/geom/LineString'

declare module 'state/map.js' {
  export type LegendURL = {
    harvestId: number
    name: string
    url: string
  }

  export type legendData = {
    c: string[]
    q: number[]
    num_ranges: number
    palette: string
  }

  export type Legend = {
    id: number
    tenantId: string
    harvestId: number | null
    cropId: number | null
    legendData: legendData & {
      styleType?: 'gradient' | 'hillshade_color' | 'hillshade'
    }
    parameter: string
  }

  export type WMSLayer = {
    id: number
    name: string
    bbox: number[]
    legendUrl: string
    /** layer_info's legend URL; legendUrl may carry a chosen variant. */
    layerLegendUrl?: string
    harvestId: number
    tenantId: string
    layerZIndex: number
    swathParameter: string
    queryable: boolean
    customLegends?: Legend[]
    zindex: number
  }

  export type PointInfoResult = {
    crop?: string
    Crop?: string
    yldt_ha?: number
    YLDt_ha?: number
    yldbu_ac?: number
    YLDbu_ac?: number
    yldkg_ha?: number
    YLDkg_ha?: number
    moisture?: number
    Moisture?: number
    speedkmh?: number
    SpeedKmh?: number
    harvestTimestamp?: string
    latitude?: number
    Latitude?: number
    longitude?: number
    Longitude?: number
    combine?: string
    Combine?: string
    headerw_m?: number
    HeaderW_M?: number
    temperatur?: number
    Temperatur?: number
    timedel?: number
    TimeDel?: number
    calibr?: number
    Calibr?: number
    ym_id?: string
    YM_id?: string
  }

  export type HarvestSwathParameter = {
    harvestId: number
    swathParameter: string
  }

  export interface Map {
    editField: boolean
    drawBoundary: boolean
    defaultExtent: number[]
    extent: number | undefined
    editBoundary: boolean
    drawHole: boolean
    unsavedChanges: boolean
    drawType: string
    newFeature: undefined
    center: number[]
    zoom: number
    pointInfoEnabled: boolean
    pointInfoReq: undefined | number[]
    pointInfoRes: symbol | PointInfoResult
    areaInfoReq: undefined
    areaInfoRes: symbol
    elevationProfileReq: LineString | undefined
    elevationProfileRes: symbol | undefined
    addNote: undefined
    noteText: undefined
    noteFeatures: object
    legendUrl: LegendURL[]
    legendUrl_a: LegendURL[]
    legendUrl_b: LegendURL[]
    wmsLayers: WMSLayer[]
    wmsLayersA: WMSLayer[]
    wmsLayersB: WMSLayer[]
    swathParameters: HarvestSwathParameter[]
    swathParameterA: HarvestSwathParameter[]
    swathParameterB: HarvestSwathParameter[]
    layerOpacity: number
    outsideBoundaryPointsVisible: boolean
    outsideBoundaryPointsOpacity: number
    threeDEnabled: boolean
    threeDSurface: ThreeDSurface
    threeDStatus: ThreeDStatus
    verticalExaggeration: number
  }
}

export type ThreeDSurface =
  (typeof c.THREE_D_SURFACE)[keyof typeof c.THREE_D_SURFACE]

export type ThreeDStatus =
  (typeof c.THREE_D_STATUS)[keyof typeof c.THREE_D_STATUS]

const DEFAULT_CENTER = [22.27825982386202, 37.343854737018034]
const DEFAULT_ZOOM = 4

const initState = {
  editField: false,
  drawBoundary: false,
  editBoundary: false,
  extent: undefined,
  drawHole: false,
  unsavedChanges: false,
  drawType: 'MultiPolygon',
  newFeature: undefined,
  center: DEFAULT_CENTER,
  zoom: DEFAULT_ZOOM,
  pointInfoEnabled: true,
  pointInfoReq: undefined,
  pointInfoRes: c.UNDEFINED,
  areaInfoReq: undefined,
  areaInfoRes: c.UNDEFINED,
  elevationProfileReq: undefined,
  elevationProfileRes: c.UNDEFINED,
  addNote: undefined,
  noteText: undefined,
  noteFeatures: new Map(),
  legendUrl: [],
  legendUrl_a: [],
  legendUrl_b: [],
  wmsLayers: [],
  wmsLayersA: [],
  wmsLayersB: [],
  swathParameters: [],
  swathParameterA: [],
  swathParameterB: [],
  layerOpacity: 1.0,
  outsideBoundaryPointsVisible: false,
  outsideBoundaryPointsOpacity: 1.0,
  // 3D is opt-in: it pulls a multi-megabyte Cesium bundle and a WebGL context,
  // so it is never entered without an explicit toggle.
  threeDEnabled: false,
  threeDSurface: c.THREE_D_SURFACE.IMAGERY,
  threeDStatus: c.THREE_D_STATUS.IDLE,
  verticalExaggeration: c.DEFAULT_VERTICAL_EXAGGERATION,
}

export default (state = initState, action) => {
  switch (action.type) {
    case c.MAP_CENTER: {
      //@ts-expect-error
      return update(state, { center: { $set: action.payload } })
    }

    case c.MAP_ZOOM: {
      //@ts-expect-error
      return update(state, { zoom: { $set: action.payload } })
    }

    case c.DRAW_BOUNDARY: {
      //@ts-expect-error
      return update(state, {
        drawBoundary: { $set: action.payload },
        editBoundary: { $set: false },
        drawHole: { $set: false },
      })
    }

    case c.DRAW_HOLE: {
      //@ts-expect-error
      return update(state, {
        drawHole: { $set: action.payload },
        editBoundary: { $set: false },
        drawBoundary: { $set: false },
      })
    }

    case c.SET_NEW_FEATURE: {
      //@ts-expect-error
      return update(state, { newFeature: { $set: action.payload } })
    }

    case c.SET_DRAW_TYPE: {
      //@ts-expect-error
      return update(state, { drawType: { $set: action.payload } })
    }

    case c.SET_EDIT_FIELD: {
      //@ts-expect-error
      return update(state, { editField: { $set: action.payload } })
    }

    case c.EDIT_BOUNDARY: {
      //@ts-expect-error
      return update(state, {
        editBoundary: { $set: action.payload },
        drawBoundary: { $set: false },
        drawHole: { $set: false },
      })
    }

    case c.BOUNDARY_CHANGES_MADE: {
      //@ts-expect-error
      return update(state, {
        unsavedBoundary: { $set: action.payload },
      })
    }

    case c.POINT_INFO_ENABLED: {
      const layers: WMSLayer[] = get(state, 'wmsLayers', [])
      //@ts-expect-error
      return update(state, {
        pointInfoEnabled: { $set: layers.some((layer) => layer.queryable) },
      })
    }

    case c.POINT_INFO_REQ: {
      //@ts-expect-error
      return update(state, { pointInfoReq: { $set: action.payload } })
    }

    case c.POINT_INFO_RES: {
      //@ts-expect-error
      return update(state, { pointInfoRes: { $set: action.payload } })
    }

    case c.POINT_INFO_CLR: {
      //@ts-expect-error
      return update(state, {
        pointInfoReq: { $set: undefined },
        pointInfoRes: { $set: c.UNDEFINED },
      })
    }

    case c.SWATH_INFO_REQ: {
      //@ts-expect-error
      return update(state, { swathInfoReq: { $set: action.payload } })
    }

    case c.SWATH_INFO_RES: {
      //@ts-expect-error
      return update(state, { swathInfoRes: { $set: action.payload } })
    }

    case c.SWATH_INFO_CLR: {
      //@ts-expect-error
      return update(state, {
        swathInfoReq: { $set: undefined },
        swathInfoRes: { $set: c.UNDEFINED },
      })
    }

    case c.AREA_INFO_REQ: {
      //@ts-expect-error
      return update(state, { areaInfoReq: { $set: action.payload } })
    }

    case c.AREA_INFO_RES: {
      //@ts-expect-error
      return update(state, { areaInfoRes: { $set: action.payload } })
    }

    case c.AREA_INFO_CLR: {
      //@ts-expect-error
      return update(state, {
        areaInfoReq: { $set: undefined },
        areaInfoRes: { $set: c.UNDEFINED },
      })
    }

    case c.ELEVATION_PROFILE_REQ: {
      //@ts-expect-error
      return update(state, { elevationProfileReq: { $set: action.payload } })
    }

    case c.ELEVATION_PROFILE_RES: {
      //@ts-expect-error
      return update(state, { elevationProfileRes: { $set: action.payload } })
    }

    case c.ELEVATION_PROFILE_CLR: {
      //@ts-expect-error
      return update(state, {
        elevationProfileReq: { $set: undefined },
        elevationProfileRes: { $set: c.UNDEFINED },
      })
    }

    case c.SET_LAYER_LEGEND: {
      const layerLegends: LegendURL[] = get(state, 'legendUrl', [])
      const index = findIndex(
        layerLegends,
        (layer) =>
          layer.name === action.payload.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          legendUrl: { $splice: [[index, 1, action.payload]] },
        })
      } //@ts-expect-error
      else return update(state, { legendUrl: { $unshift: [action.payload] } })
    }

    case c.REMOVE_LAYER_LEGEND: {
      const layerLegends: LegendURL[] = get(state, 'legendUrl', [])
      const idx = findIndex(
        layerLegends,
        (layer) =>
          layer.name === action?.payload?.name &&
          layer.harvestId === action?.payload?.harvestId,
      )

      if (idx > -1) {
        //@ts-expect-error
        return update(state, { legendUrl: { $splice: [[idx, 1]] } })
      } else if (action.payload === undefined) {
        //@ts-expect-error
        return update(state, { legendUrl: { $set: [] } })
      } else {
        //@ts-expect-error

        return update(state, {
          legendUrl: { $set: get(state, 'legendUrl', []) },
        })
      }
    }

    case c.REMOVE_LAYER_LEGEND_A: {
      const layerLegends: LegendURL[] = get(state, 'legendUrl_a', [])
      const idx = findIndex(
        layerLegends,
        (layer) =>
          layer.name === action?.payload?.name &&
          layer.harvestId === action?.payload?.harvestId,
      )

      if (idx > -1) {
        //@ts-expect-error
        return update(state, { legendUrl_a: { $splice: [[idx, 1]] } })
      } else if (action.payload === undefined) {
        //@ts-expect-error
        return update(state, { legendUrl_a: { $set: [] } })
      } else {
        //@ts-expect-error
        return update(state, {
          legendUrl_a: { $set: get(state, 'legendUrl', []) },
        })
      }
    }

    case c.REMOVE_LAYER_LEGEND_B: {
      const layerLegends: LegendURL[] = get(state, 'legendUrl_b', [])
      const idx = findIndex(
        layerLegends,
        (layer) =>
          layer.name === action?.payload?.name &&
          layer.harvestId === action?.payload?.harvestId,
      )

      if (idx > -1) {
        //@ts-expect-error
        return update(state, { legendUrl_b: { $splice: [[idx, 1]] } })
      } else if (action.payload === undefined) {
        //@ts-expect-error
        return update(state, { legendUrl_b: { $set: [] } })
      } else {
        //@ts-expect-error
        return update(state, {
          legendUrl_b: { $set: get(state, 'legendUrl_b', []) },
        })
      }
    }

    case c.REMOVE_LAYER_LEGENDS: {
      //@ts-expect-error
      return update(state, {
        legendUrl: { $set: [] },
        legendUrl_a: { $set: [] },
        legendUrl_b: { $set: [] },
      })
    }

    case c.REMOVE_LAYER_LEGENDS_A: {
      //@ts-expect-error
      return update(state, {
        legendUrl_a: { $set: [] },
      })
    }

    case c.REMOVE_LAYER_LEGENDS_B: {
      //@ts-expect-error
      return update(state, {
        legendUrl_b: { $set: [] },
      })
    }

    case c.SET_LAYER_A: {
      const layers: WMSLayer[] = get(state, 'wmsLayersA', [])
      const index = findIndex(
        layers,
        (layer) =>
          layer.name === action.payload.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          wmsLayersA: { $splice: [[index, 1, action.payload]] },
        })
      }
      //@ts-expect-error
      else return update(state, { wmsLayersA: { $unshift: [action.payload] } })
    }

    case c.SET_SWATH_PARAMETER_A: {
      const layers: WMSLayer[] = get(state, 'wmsLayersA', [])
      const index = findIndex(
        layers,
        (layer) =>
          // layer.name === action.payload.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (Object.keys(action.payload).length === 0) {
        //@ts-expect-error
        return update(state, { swathParameterA: { $set: [] } })
      }

      if (index > -1) {
        const layer = cloneDeep(layers[index])
        const newSld = layer.legendUrl.replace(
          /\.sld$/,
          action.payload.swathParameterSldExt,
        )

        layer.legendUrl = newSld

        const swathParameters: HarvestSwathParameter[] = get(
          state,
          'swathParameterA',
          [],
        )
        const swathIndex = findIndex(
          swathParameters,
          (sp) => sp.harvestId === action.payload.harvestId,
        )
        const stateUpdate = {}
        if (swathIndex >= 0) {
          stateUpdate['swathParameterA'] = {
            $splice: [
              [
                swathIndex,
                1,
                {
                  swathParameter: action.payload.swathParameter,
                  harvestId: action.payload.harvestId,
                  swathParameterSldExt: action.payload.swathParameterSldExt,
                },
              ],
            ],
          }
        } else {
          stateUpdate['swathParameterA'] = {
            $push: [
              {
                swathParameter: action.payload.swathParameter,
                harvestId: action.payload.harvestId,
                swathParameterSldExt: action.payload.swathParameterSldExt,
              },
            ],
          }
        }
        stateUpdate['wmsLayersA'] = {
          $splice: [[index, 1, layer]],
        }
        //@ts-expect-error
        return update(state, stateUpdate)
      } else {
        console.log('set swath parameter A layer not found')
      }
    }

    case c.SET_LAYER_B: {
      const layers: WMSLayer[] = get(state, 'wmsLayersB', [])
      const index = findIndex(
        layers,
        (layer) =>
          layer.name === action.payload.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          wmsLayersB: { $splice: [[index, 1, action.payload]] },
        })
      }
      //@ts-expect-error
      else return update(state, { wmsLayersB: { $unshift: [action.payload] } })
    }

    case c.SET_SWATH_PARAMETER_B: {
      const layers: WMSLayer[] = get(state, 'wmsLayersB', [])
      const index = findIndex(
        layers,
        (layer) =>
          // layer.name === action.payload.name &&
          layer.harvestId === action?.payload?.harvestId,
      )

      if (Object.keys(action.payload).length === 0) {
        //@ts-expect-error
        return update(state, { swathParameterB: { $set: [] } })
      }

      if (index > -1) {
        const layer = cloneDeep(layers[index])
        const newSld = layer?.legendUrl?.replace(
          /\.sld$/,
          action.payload.swathParameterSldExt,
        )

        layer.legendUrl = newSld

        const swathParameters: HarvestSwathParameter[] = get(
          state,
          'swathParameterB',
          [],
        )
        const swathIndex = findIndex(
          swathParameters,
          (sp) => sp.harvestId === action.payload.harvestId,
        )
        const stateUpdate = {}
        if (swathIndex >= 0) {
          stateUpdate['swathParameterB'] = {
            $splice: [
              [
                swathIndex,
                1,
                {
                  swathParameter: action.payload.swathParameter,
                  harvestId: action.payload.harvestId,
                  swathParameterSldExt: action.payload.swathParameterSldExt,
                },
              ],
            ],
          }
        } else {
          stateUpdate['swathParameterB'] = {
            $push: [
              {
                swathParameter: action.payload.swathParameter,
                harvestId: action.payload.harvestId,
                swathParameterSldExt: action.payload.swathParameterSldExt,
              },
            ],
          }
        }
        stateUpdate['wmsLayersB'] = {
          $splice: [[index, 1, layer]],
        }
        //@ts-expect-error
        return update(state, stateUpdate)
      } else {
        console.log('set swath parameter B layer not found')
      }
    }

    case c.SET_LAYER: {
      const layers: WMSLayer[] = get(state, 'wmsLayers', [])
      const index = findIndex(
        layers,
        (layer) =>
          layer.name === action.payload.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          wmsLayers: { $splice: [[index, 1, action.payload]] },
        })
      } //@ts-expect-error
      else return update(state, { wmsLayers: { $unshift: [action.payload] } })
    }

    case c.SET_SWATH_PARAMETER: {
      const stateUpdate = {}
      const swathParameters: HarvestSwathParameter[] = get(
        state,
        'swathParameters',
        [],
      )
      const swathIndex = findIndex(
        swathParameters,
        (sp) => sp.harvestId === action.payload.harvestId,
      )
      if (swathIndex >= 0) {
        stateUpdate['swathParameters'] = {
          $splice: [
            [
              swathIndex,
              1,
              {
                swathParameter: action.payload.swathParameter,
                harvestId: action.payload.harvestId,
                swathParameterSldExt: action.payload.swathParameterSldExt,
              },
            ],
          ],
        }
      } else {
        stateUpdate['swathParameters'] = {
          $push: [
            {
              swathParameter: action.payload.swathParameter,
              harvestId: action.payload.harvestId,
              swathParameterSldExt: action.payload.swathParameterSldExt,
            },
          ],
        }
      }
      //@ts-expect-error
      return update(state, stateUpdate)
    }

    case c.UPDATE_LAYER: {
      const layers: WMSLayer[] = cloneDeep(get(state, 'wmsLayers', []))
      const index = findIndex(
        layers,
        (layer) =>
          layer.name === action.payload.layerName &&
          layer.harvestId === action?.payload?.harvestId,
      )

      if (index > -1) {
        const layer = layers[index]
        if (layer.name === 'swath') {
          const swathParameters: HarvestSwathParameter[] = get(
            state,
            'swathParameters',
            [],
          )
          const selectedSwath =
            swathParameters?.find((s) => s.harvestId === layer.harvestId)
              ?.swathParameter || 'yield'
          const swathIndex = findIndex(
            layer?.customLegends,
            (cl) => cl.parameter === selectedSwath,
          )
          const response = action.payload?.customLegends?.find(
            (a) => a.parameter === selectedSwath,
          )
          if (layer.customLegends) {
            if (response && swathIndex > -1) {
              layer.customLegends[swathIndex] = response
            } else if (response) {
              layer.customLegends.push(response)
            } else {
              layer.customLegends = []
            }
          }

          //@ts-expect-error
          return update(state, {
            wmsLayers: { $splice: [[index, 1, layer]] },
          })
        } else {
          const layer = layers[index]
          // For non-swath layers (including DEM), replace entire customLegends array
          layer.customLegends = action.payload?.customLegends || []

          //@ts-expect-error
          return update(state, { wmsLayers: { $splice: [[index, 1, layer]] } })
        }
      }
    }

    case c.CROP_LEGEND_UPDATED: {
      const legendUpdate = action.payload.data
      const harvestsToUpdate = action.payload.harvestIds
      let stateUpdate: WMSLayer[] = []
      let updateNeeded = false
      if (legendUpdate?.cropId && harvestsToUpdate?.length > 0) {
        const layers: WMSLayer[] = cloneDeep(get(state, 'wmsLayers', []))

        // Every layer is kept; only the in-scope ones are modified. stateUpdate
        // replaces wmsLayers wholesale below, so collecting just the matches
        // would drop every other layer from the map.
        stateUpdate = layers

        layers.forEach((layer) => {
          // Must be AND: the old OR matched any layer that merely had a
          // customLegends array, so a crop legend applied to harvests from other
          // seasons and other crops.
          if (harvestsToUpdate.includes(layer.harvestId)) {
            const legendIndex = findIndex(
              layer.customLegends,
              (cl) =>
                cl.cropId === legendUpdate.cropId &&
                cl.parameter === legendUpdate.parameter,
            )
            if (legendIndex > -1 && layer.customLegends) {
              updateNeeded = true
              layer.customLegends[legendIndex] = legendUpdate
            } else if (layer.customLegends) {
              updateNeeded = true
              layer.customLegends.push(legendUpdate)
            } else {
              updateNeeded = true
              layer.customLegends = [legendUpdate]
            }
          }
        })
      }
      if (updateNeeded) {
        //@ts-expect-error
        return update(state, { wmsLayers: { $set: stateUpdate } })
      } else {
        return state
      }
    }

    case c.REMOVE_LAYER: {
      const layers: WMSLayer[] = get(state, 'wmsLayers', [])
      const idx = findIndex(
        layers,
        (layer) =>
          layer.name === action?.payload?.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (idx > -1) {
        //@ts-expect-error
        return update(state, { wmsLayers: { $splice: [[idx, 1]] } })
      } else if (action.payload === undefined) {
        //@ts-expect-error
        return update(state, { wmsLayers: { $set: [] } })
      } else {
        //@ts-expect-error
        return update(state, {
          wmsLayers: { $set: get(state, 'wmsLayers', []) },
        })
      }
    }

    case c.REMOVE_LAYER_A: {
      const layers: WMSLayer[] = get(state, 'wmsLayersA', [])
      const idx = findIndex(
        layers,
        (layer) =>
          layer.name === action?.payload?.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (idx > -1) {
        //@ts-expect-error
        return update(state, { wmsLayersA: { $splice: [[idx, 1]] } })
      } else if (action.payload === undefined) {
        //@ts-expect-error
        return update(state, { wmsLayersA: { $set: [] } })
      } else {
        //@ts-expect-error
        return update(state, {
          wmsLayersA: { $set: get(state, 'wmsLayersA', []) },
        })
      }
    }

    case c.REMOVE_LAYER_B: {
      const layers: WMSLayer[] = get(state, 'wmsLayersB', [])
      const idx = findIndex(
        layers,
        (layer) =>
          layer.name === action?.payload?.name &&
          layer.harvestId === action?.payload?.harvestId,
      )
      if (idx > -1) {
        //@ts-expect-error
        return update(state, { wmsLayersB: { $splice: [[idx, 1]] } })
      } else if (action.payload === undefined) {
        //@ts-expect-error
        return update(state, { wmsLayersB: { $set: [] } })
      } else {
        //@ts-expect-error
        return update(state, {
          wmsLayersB: { $set: get(state, 'wmsLayersB', []) },
        })
      }
    }

    case c.RESET_LAYERS: {
      //@ts-expect-error
      return update(state, { wmsLayers: { $set: [] }, legendUrl: { $set: [] } })
    }

    case c.RESET_LAYERS_A: {
      //@ts-expect-error
      return update(state, {
        wmsLayersA: { $set: [] },
        legendUrl_a: { $set: [] },
      })
    }

    case c.RESET_LAYERS_B: {
      //@ts-expect-error
      return update(state, {
        wmsLayersB: { $set: [] },
        legendUrl_b: { $set: [] },
      })
    }

    case c.SET_PREVIEW_LEGEND: {
      //@ts-expect-error
      return update(state, { previewLegend: { $set: action.payload } })
    }

    case c.SET_LAYER_LEGEND_A: {
      const layerLegends: LegendURL[] = get(state, 'legendUrl_a', [])
      const index = findIndex(
        layerLegends,
        (layer) =>
          layer.name === action.payload.name &&
          layer?.harvestId === action?.payload?.harvestId,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          legendUrl_a: { $splice: [[index, 1, action.payload]] },
        })
      }
      //@ts-expect-error
      else return update(state, { legendUrl_a: { $unshift: [action.payload] } })
    }

    case c.SET_LAYER_LEGEND_B: {
      const layerLegends: LegendURL[] = get(state, 'legendUrl_b', [])
      const index = findIndex(
        layerLegends,
        (layer) =>
          layer.name === action.payload.name &&
          layer?.harvestId === action?.payload?.harvestId,
      )
      if (index > -1) {
        //@ts-expect-error
        return update(state, {
          legendUrl_b: { $splice: [[index, 1, action.payload]] },
        })
      }
      //@ts-expect-error
      else return update(state, { legendUrl_b: { $unshift: [action.payload] } })
    }

    case c.SET_EXTENT: {
      //@ts-expect-error
      return update(state, { extent: { $set: action.payload } })
    }

    case c.SET_LAYER_OPACITY: {
      //@ts-expect-error
      return update(state, { layerOpacity: { $set: action.payload } })
    }

    case c.OUTSIDE_BOUNDARY_POINTS_LAYER_TOGGLE: {
      //@ts-expect-error
      return update(state, {
        outsideBoundaryPointsVisible: {
          $set: !state.outsideBoundaryPointsVisible,
        },
      })
    }

    case c.OUTSIDE_BOUNDARY_POINTS_LAYER_HIDE: {
      //@ts-expect-error
      return update(state, { outsideBoundaryPointsVisible: { $set: false } })
    }

    case c.SET_OUTSIDE_BOUNDARY_POINTS_OPACITY: {
      //@ts-expect-error
      return update(state, {
        outsideBoundaryPointsOpacity: { $set: action.payload },
      })
    }

    case c.THREE_D_TOGGLE: {
      //@ts-expect-error
      return update(state, { threeDEnabled: { $set: !state.threeDEnabled } })
    }

    case c.THREE_D_SET_ENABLED: {
      //@ts-expect-error
      return update(state, { threeDEnabled: { $set: !!action.payload } })
    }

    case c.THREE_D_SET_SURFACE: {
      //@ts-expect-error
      return update(state, { threeDSurface: { $set: action.payload } })
    }

    case c.THREE_D_SET_STATUS: {
      //@ts-expect-error
      return update(state, { threeDStatus: { $set: action.payload } })
    }

    case c.SET_VERTICAL_EXAGGERATION: {
      //@ts-expect-error
      return update(state, { verticalExaggeration: { $set: action.payload } })
    }

    default:
      return state
  }
}
